import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { X, Plus, MessageCircle, Phone, ArrowLeft, GitCompareArrows, Search } from "lucide-react";
import api, { formatPriceRange, resolveAsset } from "@/lib/api";
import LeadModal from "@/components/site/LeadModal";
import PageSeo from "@/components/site/PageSeo";
import { useSettings, usePageCopy } from "@/contexts/SettingsContext";
import {
    resolveWhatsAppDigits,
    resolveTelHref,
    whatsappUrl,
    bikeInquiryMessage,
} from "@/lib/contact";
import {
    getCompareIds,
    setCompareIds,
    removeFromCompare,
    addToCompare,
    MAX_COMPARE,
} from "@/lib/compare";

const ROWS = [
    { key: "price_min", label: "Price (min)", short: "Price", fmt: (v) => `₹${v?.toLocaleString("en-IN")}` },
    { key: "fuel_type", label: "Fuel Type", short: "Fuel" },
    { key: "engine_cc", label: "Engine", short: "Engine", fmt: (v) => v ? `${v} cc` : "—" },
    { key: "power_bhp", label: "Power", short: "Power", fmt: (v) => `${v} bhp` },
    { key: "torque_nm", label: "Torque", short: "Torque", fmt: (v) => `${v} Nm` },
    { key: "mileage", label: "Mileage", short: "Mileage", fmt: (v) => v ? `${v} kmpl` : "—" },
    { key: "range_km", label: "Range", short: "Range", fmt: (v) => v ? `${v} km` : "—" },
    { key: "battery_kwh", label: "Battery", short: "Battery", fmt: (v) => v ? `${v} kWh` : "—" },
    { key: "weight", label: "Weight", short: "Weight", fmt: (v) => `${v} kg` },
    { key: "fuel_tank", label: "Fuel Tank", short: "Tank", fmt: (v) => v ? `${v} L` : "—" },
    { key: "abs", label: "ABS", short: "ABS", fmt: (v) => v ? "Yes" : "No" },
    { key: "bluetooth", label: "Bluetooth", short: "Bluetooth", fmt: (v) => v ? "Yes" : "—" },
    { key: "rating", label: "Rating", short: "Rating", fmt: (v) => `${v?.toFixed(1)} / 5` },
];

const cellValue = (row, bike) => (row.fmt ? row.fmt(bike[row.key]) : (bike[row.key] ?? "—"));

const COLS = "w-full min-w-[36rem] table-fixed";
const LABEL = "w-[8.5rem] min-w-[8.5rem]";
const BIKE = "min-w-[8.5rem]";

const FilledSlot = ({ bike, onRemove, compact = false }) => (
    <div className="relative text-left">
        <button
            type="button"
            onClick={() => onRemove(bike.id)}
            className="absolute top-1.5 right-1.5 z-10 h-8 w-8 rounded-full bg-white border border-[#e8eaee] text-[#0A2540] hover:bg-[#E31837] hover:text-white hover:border-[#E31837] flex items-center justify-center"
            aria-label={`Remove ${bike.name}`}
            data-testid={`compare-remove-${bike.id}`}
        >
            <X size={14} />
        </button>
        <div className={`aspect-[4/3] rounded-xl overflow-hidden bg-[#F4F6F8] ${compact ? "mb-2" : "mb-3"}`}>
            <img src={resolveAsset(bike.image)} alt="" className="w-full h-full object-cover" />
        </div>
        <p className={compact ? "text-[12px] text-[#7a8597]" : "text-[10px] uppercase tracking-wide text-[#7a8597]"}>{bike.brand}</p>
        <p className={`font-display font-bold leading-tight text-[#0A2540] ${compact ? "text-[15px]" : "text-sm pr-2"}`}>{bike.name}</p>
        <p className={`font-display font-bold text-[#E31837] mt-1 ${compact ? "text-sm" : "text-sm"}`}>{formatPriceRange(bike.price_min, bike.price_max)}</p>
        {compact ? (
            <Link to={`/bikes/${bike.id}`} className="inline-flex items-center min-h-[44px] text-[13px] font-semibold text-[#E31837]">View details</Link>
        ) : (
            <Link to={`/bikes/${bike.id}`} className="btn btn-white btn-sm w-full justify-center mt-3 !px-2 !text-xs">View details</Link>
        )}
    </div>
);

const EmptySlot = ({ onClick, testId, label }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className="h-full min-h-[9rem] sm:min-h-[200px] w-full rounded-xl border-2 border-dashed border-[#e8eaee] flex flex-col items-center justify-center text-[#7a8597] hover:text-[#E31837] hover:border-[#E31837] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E31837]"
    >
        <Plus size={28} />
        <span className="mt-2 text-sm font-medium">{label}</span>
    </button>
);

const ComparePage = () => {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [bikes, setBikes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pool, setPool] = useState([]);
    const [poolReady, setPoolReady] = useState(false);
    const [addQuery, setAddQuery] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const [leadOpen, setLeadOpen] = useState(false);
    const searchRef = useRef(null);
    const searchBoxRef = useRef(null);
    const headRef = useRef(null);
    const bodyRef = useRef(null);
    const phoneRef = useRef(null);
    const [phoneSize, setPhoneSize] = useState({ frame: 320, col: 160 });
    const [track, setTrack] = useState({ overflow: false, thumb: 100, offset: 0 });
    const { settings } = useSettings();
    const copy = usePageCopy("compare", { title: "Compare bikes side by side" });

    const ids = useMemo(() => {
        const fromUrl = params.get("ids");
        if (fromUrl) return fromUrl.split(",").filter(Boolean).slice(0, MAX_COMPARE);
        return getCompareIds();
    }, [params]);

    useEffect(() => {
        const fromUrl = params.get("ids");
        if (fromUrl) setCompareIds(fromUrl.split(",").filter(Boolean));
    }, [params]);

    useEffect(() => {
        if (ids.length === 0) {
            setBikes([]);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        Promise.all(ids.map((id) => api.get(`/bikes/${id}`).then((r) => r.data).catch(() => null)))
            .then((list) => {
                if (!cancelled) setBikes(list.filter(Boolean));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [ids]);

    useEffect(() => {
        if (ids.length >= MAX_COMPARE || pool.length > 0) return undefined;
        let cancelled = false;
        api.get("/bikes?limit=100").then((r) => {
            if (!cancelled) setPool(r.data.items || []);
        }).catch(() => {}).finally(() => {
            if (!cancelled) setPoolReady(true);
        });
        return () => { cancelled = true; };
    }, [ids.length, pool.length]);

    const addCandidates = useMemo(() => {
        const q = addQuery.trim().toLowerCase();
        return pool
            .filter((p) => !bikes.find((b) => b.id === p.id) && !ids.includes(p.id))
            .filter((p) => !q || p.name.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q))
            .slice(0, 12);
    }, [pool, bikes, ids, addQuery]);

    const syncIds = (nextIds) => {
        setCompareIds(nextIds);
        if (nextIds.length) navigate(`/compare?ids=${nextIds.join(",")}`, { replace: true });
        else navigate("/compare", { replace: true });
    };

    const remove = (id) => syncIds(removeFromCompare(id));

    const add = (b) => {
        const result = addToCompare(b.id);
        if (!result.added) return;
        syncIds(result.ids);
        setAddQuery("");
        setSearchOpen(false);
        searchRef.current?.blur();
    };

    const focusSearch = () => {
        searchRef.current?.focus();
        setSearchOpen(true);
        searchRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    };

    const syncScroll = (source, target) => {
        if (!target || target.scrollLeft === source.scrollLeft) return;
        target.scrollLeft = source.scrollLeft;
    };

    const activeScroller = () => {
        const phone = phoneRef.current;
        if (phone && phone.offsetParent !== null) return phone;
        return headRef.current;
    };

    const measureTrack = () => {
        const phone = phoneRef.current;
        if (phone && phone.offsetParent !== null) {
            const frame = phone.clientWidth;
            const col = Math.max(1, Math.floor(frame / 2));
            setPhoneSize((prev) => (prev.frame === frame && prev.col === col ? prev : { frame, col }));
        }
        const el = activeScroller();
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        const overflow = max > 2;
        const thumb = overflow ? Math.max(28, (el.clientWidth / el.scrollWidth) * 100) : 100;
        const offset = overflow ? (el.scrollLeft / max) * (100 - thumb) : 0;
        setTrack((prev) => (
            prev.overflow === overflow && Math.abs(prev.thumb - thumb) < 0.4 && Math.abs(prev.offset - offset) < 0.4
                ? prev
                : { overflow, thumb, offset }
        ));
    };

    useLayoutEffect(() => {
        measureTrack();
        const nodes = [phoneRef.current, headRef.current].filter(Boolean);
        if (!nodes.length) return undefined;
        const ro = new ResizeObserver(measureTrack);
        nodes.forEach((node) => ro.observe(node));
        window.addEventListener("resize", measureTrack);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", measureTrack);
        };
    });

    const names = bikes.map((b) => b.name).join(" vs ");
    const compareNote = names
        ? `Comparing: ${names}. Please help me decide and call back.`
        : "I need help choosing between bikes.";
    const waHref = whatsappUrl(
        resolveWhatsAppDigits(settings),
        names
            ? `Hi Servall Bikes, I'm comparing ${names}. Can an expert help me decide?`
            : bikeInquiryMessage(null, "compare_help"),
    );
    const telHref = resolveTelHref(settings);
    const openSlots = bikes.length < MAX_COMPARE;
    const showTable = bikes.length >= 2;

    const slot = (index) => {
        const bike = bikes[index];
        if (bike) return <FilledSlot bike={bike} onRemove={remove} />;
        if (!openSlots) return null;
        const firstEmpty = bikes.length;
        return (
            <EmptySlot
                onClick={focusSearch}
                label="Add bike"
                testId={index === firstEmpty ? "compare-add-slot" : undefined}
            />
        );
    };

    return (
        <div data-testid="compare-page" className="bg-[#F4F6F8]">
            <PageSeo pageKey="compare" />
            <div className="bg-[#0A2540] text-white">
                <div className="container-x py-8 md:py-10">
                    <Link to="/bikes" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white mb-4 min-h-[44px]">
                        <ArrowLeft size={16} /> Back to all bikes
                    </Link>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#E31837] flex items-center justify-center shrink-0">
                            <GitCompareArrows size={20} />
                        </div>
                        <p className="text-xs uppercase tracking-widest text-white/60">{copy.eyebrow || "Decision tool"}</p>
                    </div>
                    <h1 className="font-display font-bold text-[1.65rem] leading-[1.15] sm:text-3xl mt-3 max-w-3xl" style={{ textWrap: "balance" }}>
                        {copy.title}
                    </h1>
                    <p className="text-sm leading-relaxed text-white/75 mt-2 max-w-2xl" style={{ textWrap: "pretty" }}>
                        {copy.subtitle || "Add up to 3 bikes and compare price, engine, mileage, and features."}
                    </p>
                </div>
            </div>

            <section className="container-x py-8 md:py-10">
                {loading && bikes.length === 0 ? (
                    <p className="text-sm text-[#7a8597]">Loading comparison…</p>
                ) : (
                    <>
                        {openSlots && (
                            <div ref={searchBoxRef} className="relative z-30 mb-4" data-testid="compare-quick-add">
                                <div className="flex items-center gap-2">
                                    <div className="input-with-icon flex-1 min-w-0">
                                        <Search size={16} className="text-[#98a0aa]" aria-hidden />
                                        <input
                                            ref={searchRef}
                                            className="input"
                                            placeholder="Search bike or brand…"
                                            value={addQuery}
                                            onChange={(e) => setAddQuery(e.target.value)}
                                            onFocus={() => setSearchOpen(true)}
                                            onBlur={(e) => {
                                                if (searchBoxRef.current?.contains(e.relatedTarget)) return;
                                                setSearchOpen(false);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Escape") {
                                                    setSearchOpen(false);
                                                    e.currentTarget.blur();
                                                }
                                            }}
                                            data-testid="compare-add-search"
                                            aria-expanded={searchOpen}
                                            aria-controls="compare-add-list"
                                        />
                                    </div>
                                    <Link to="/bikes" className="shrink-0 text-sm font-medium text-[#E31837] min-h-[44px] inline-flex items-center whitespace-nowrap" data-testid="compare-add-another">Browse All Bikes</Link>
                                </div>
                                {searchOpen && (
                                    <ul
                                        id="compare-add-list"
                                        className="absolute left-0 right-0 top-full z-40 mt-1 max-h-52 overflow-y-auto divide-y divide-[#eef0f3] rounded-xl border border-[#e8eaee] bg-white shadow-[0_12px_28px_rgba(10,37,64,0.12)]"
                                        onMouseDown={(e) => e.preventDefault()}
                                    >
                                        {!poolReady ? (
                                            <li className="p-3 text-sm text-[#7a8597]">Loading bikes…</li>
                                        ) : addCandidates.length === 0 ? (
                                            <li className="p-3 text-sm text-[#7a8597]">No matches. Browse All Bikes to filter the full list.</li>
                                        ) : addCandidates.map((b) => (
                                            <li key={b.id}>
                                                <button
                                                    type="button"
                                                    onClick={() => add(b)}
                                                    className="w-full min-h-[44px] flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-[#fff5f5] text-sm"
                                                    data-testid={`compare-add-${b.id}`}
                                                >
                                                    <span className="min-w-0">
                                                        <span className="text-[#7a8597] text-xs uppercase">{b.brand}</span>
                                                        {" · "}
                                                        <strong className="text-[#0A2540]">{b.name}</strong>
                                                    </span>
                                                    <Plus size={16} className="text-[#E31837] shrink-0" />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {!showTable && (
                            <div className="rounded-2xl border border-[#e8eaee] bg-white">
                                <div className="grid grid-cols-3">
                                    {Array.from({ length: MAX_COMPARE }, (_, index) => (
                                        <div key={index} className="p-2 sm:p-4 border-l border-[#eef0f3] first:border-l-0 min-w-0">
                                            {slot(index)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {bikes.length === 1 && (
                            <p className="mt-3 text-sm text-[#7a8597]">Add one more bike to see the spec table.</p>
                        )}

                        {showTable && (
                            <>
                            <div className="md:hidden rounded-2xl border border-[#e8eaee] bg-white overflow-hidden" data-testid="compare-mobile">
                                <div
                                    ref={phoneRef}
                                    className="overflow-x-auto no-scrollbar snap-x snap-mandatory"
                                    onScroll={measureTrack}
                                >
                                    <div style={{ width: phoneSize.col * bikes.length }}>
                                        <div className="flex">
                                            {bikes.map((bike, index) => (
                                                <div key={bike.id} className={`snap-start shrink-0 box-border px-3 pt-3 ${index ? "border-l border-[#eef0f3]" : ""}`} style={{ width: phoneSize.col }}>
                                                    <FilledSlot bike={bike} onRemove={remove} compact />
                                                </div>
                                            ))}
                                        </div>
                                        <div className="sticky left-0 z-10 bg-white px-3 pb-2" style={{ width: phoneSize.frame }}>
                                            {track.overflow && (
                                                <button
                                                    type="button"
                                                    aria-label="Show the next bike"
                                                    className="block h-2 w-full rounded-full bg-[#e8eaee] p-0 border-0"
                                                    onClick={() => {
                                                        const el = phoneRef.current;
                                                        if (!el) return;
                                                        const max = el.scrollWidth - el.clientWidth;
                                                        const atEnd = el.scrollLeft >= max - 4;
                                                        el.scrollTo({ left: atEnd ? 0 : Math.min(max, el.scrollLeft + phoneSize.col), behavior: "smooth" });
                                                    }}
                                                >
                                                    <span className="block h-2 rounded-full bg-[#E31837]" style={{ width: `${track.thumb}%`, marginLeft: `${track.offset}%` }} />
                                                </button>
                                            )}
                                        </div>
                                        {ROWS.map((row) => (
                                            <div key={row.key}>
                                                <div className="sticky left-0 z-10 border-t border-[#eef0f3] bg-[#F4F6F8] px-3 py-2 text-[13px] font-semibold text-[#0A2540]" style={{ width: phoneSize.frame }}>
                                                    {row.short}
                                                </div>
                                                <div className="flex bg-white">
                                                    {bikes.map((bike, index) => (
                                                        <div key={bike.id} className={`snap-start shrink-0 box-border px-3 py-2.5 text-[15px] leading-snug font-medium text-[#0A2540] ${index ? "border-l border-[#eef0f3]" : ""}`} style={{ width: phoneSize.col }}>
                                                            {cellValue(row, bike)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="hidden md:block rounded-2xl border border-[#e8eaee] bg-white">
                                <div
                                    ref={headRef}
                                    className="compare-scroll"
                                    onScroll={(e) => {
                                        syncScroll(e.currentTarget, bodyRef.current);
                                        measureTrack();
                                    }}
                                >
                                    <table className={`${COLS} border-separate border-spacing-0`}>
                                        <colgroup>
                                            <col className={LABEL} />
                                            {Array.from({ length: MAX_COMPARE }, (_, index) => <col key={index} />)}
                                        </colgroup>
                                        <thead>
                                            <tr className="bg-[#F4F6F8]">
                                                <th className={`sticky left-0 z-20 align-bottom bg-[#F4F6F8] text-left p-3 md:p-4 text-sm font-semibold text-[#7a8597] ${LABEL} border-r border-[#e8eaee]`}>
                                                    Specs
                                                </th>
                                                {Array.from({ length: MAX_COMPARE }, (_, index) => (
                                                    <th key={index} className={`p-2 sm:p-4 align-top font-normal ${BIKE}`}>
                                                        {slot(index)}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                    </table>
                                </div>
                                {track.overflow && (
                                    <button
                                        type="button"
                                        aria-label="Show the next bike"
                                        className="mx-3 mb-2 block h-2 w-[calc(100%-1.5rem)] rounded-full bg-[#e8eaee] p-0 border-0"
                                        onClick={() => {
                                            const el = headRef.current;
                                            if (!el) return;
                                            const max = el.scrollWidth - el.clientWidth;
                                            el.scrollTo({ left: el.scrollLeft > max / 2 ? 0 : max, behavior: "smooth" });
                                        }}
                                    >
                                        <span
                                            className="block h-2 rounded-full bg-[#E31837]"
                                            style={{ width: `${track.thumb}%`, marginLeft: `${track.offset}%` }}
                                        />
                                    </button>
                                )}
                                <div
                                    ref={bodyRef}
                                    className="overflow-x-auto no-scrollbar"
                                    onScroll={(e) => syncScroll(e.currentTarget, headRef.current)}
                                >
                                    <table className={`${COLS} border-separate border-spacing-0`} data-testid="compare-table">
                                        <colgroup>
                                            <col className={LABEL} />
                                            {Array.from({ length: MAX_COMPARE }, (_, index) => <col key={index} />)}
                                        </colgroup>
                                        <tbody>
                                            {ROWS.map((row, rowIndex) => {
                                                const stripe = rowIndex % 2 === 1;
                                                return (
                                                    <tr key={row.key} className="border-t border-[#eef0f3]">
                                                        <td className={`sticky left-0 z-10 p-3 md:p-4 text-sm font-semibold text-[#7a8597] border-r border-[#e8eaee] ${LABEL} ${stripe ? "bg-[#fafafa]" : "bg-white"}`}>
                                                            {row.short}
                                                        </td>
                                                        {bikes.map((bike) => (
                                                            <td key={bike.id} className={`p-2 sm:p-4 text-sm font-medium text-[#0A2540] ${BIKE} ${stripe ? "bg-[#fafafa]" : "bg-white"}`}>
                                                                {cellValue(row, bike)}
                                                            </td>
                                                        ))}
                                                        {openSlots && Array.from({ length: MAX_COMPARE - bikes.length }, (_, i) => (
                                                            <td key={`empty-${i}`} className={`${BIKE} ${stripe ? "bg-[#fafafa]" : "bg-white"}`} />
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            </>
                        )}

                    </>
                )}

                <div className="mt-8 rounded-2xl bg-[#0A2540] text-white p-6 md:p-8" data-testid="compare-expert-cta">
                    <h2 className="font-display font-bold text-xl sm:text-2xl">Still unsure which one to buy?</h2>
                    <p className="text-sm text-white/75 mt-2 max-w-xl">
                        Talk to a Servall expert. We&apos;ll match budget, commute, and EMI, and book a test ride at your nearest outlet.
                    </p>
                    <div className="mt-5 flex flex-col sm:flex-row flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={() => setLeadOpen(true)}
                            className="btn btn-primary justify-center"
                            data-testid="compare-request-callback"
                        >
                            Request Expert Callback
                        </button>
                        <a
                            href={waHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 min-h-[44px] rounded-full bg-[#25D366] hover:bg-[#1ea952] px-5 py-2.5 text-sm font-semibold"
                            data-testid="compare-whatsapp"
                        >
                            <MessageCircle size={16} /> WhatsApp Expert
                        </a>
                        <a
                            href={telHref}
                            className="inline-flex items-center justify-center gap-2 min-h-[44px] rounded-full border border-white/30 hover:bg-white/10 px-5 py-2.5 text-sm font-semibold"
                            data-testid="compare-call"
                        >
                            <Phone size={16} /> Call
                        </a>
                    </div>
                </div>
            </section>

            <LeadModal
                open={leadOpen}
                onOpenChange={setLeadOpen}
                intent="compare_help"
                bike={bikes[0] || null}
                note={compareNote}
            />
        </div>
    );
};

export default ComparePage;
